/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */

import { BrokerOrBridge } from 'ion-types';
import createLogger from 'ion-logger';

import Topic from './topic';
import Constants from './lib/constants';
import validateTopics from './validation';
// Types needed for broker interface
import { SubscriberHandler, RegistrantHandler } from './types';

// Logging package
const logger = createLogger('ion-broker');
const recentLogger = createLogger('ion-broker:recents');
const NUM_RECENT = 25;

// The root topic that all other topics hang off of
const RootTopic = new Topic('');

class TheBroker implements BrokerOrBridge {
  recentTopics: any[] = [];
  subscriptions: any[] = [];
  registrations: any[] = [];
  /**
   * Internal logging function within Broker to keep track of the most recent
   * topics, subscriptions, registrations that have been called.
   *
   * @param {string} key
   * @param {Object} data
   * @param {int} numRecents
   */
  logToRecents = (key: string, data: any, numRecents: number) => {
    switch (key) {
      case 'recentTopics': {
        this.recentTopics.push(data);
        if (this.recentTopics.length > numRecents) {
          this.recentTopics.shift();
        }
        break;
      }
      case 'subscriptions': {
        this.subscriptions.push(data);
        if (this.subscriptions.length > numRecents) {
          this.subscriptions.shift();
        }
        break;
      }
      case 'registrations': {
        this.registrations.push(data);
        if (this.registrations.length > numRecents) {
          this.registrations.shift();
        }
        break;
      }
      default: {
        recentLogger.log(
          `Error: only logToRecents for topics, subscriptions, or registrations.`
        );
        break;
      }
    }
  };

  findLeafTopic = (topicPath: string, topicsList?: Set<Topic>): Topic => {
    if (!validateTopics(topicPath)) {
      throw new Error(
        `invalid topic provided for to findLeafTopic:
      (${topicPath})`
      );
    }

    let currentTopic = RootTopic;
    topicPath.split(Constants.CHILD_SEPARATOR).forEach(child => {
      // First see if the current 'child' can be found in the topic
      // we are currently searching
      let subTopic = currentTopic.children.get(child);
      if (!subTopic) {
        // If not, add it to the current topic under search
        subTopic = new Topic(child);
        currentTopic.children.set(child, subTopic);
      }

      currentTopic = subTopic;
      if (topicsList) {
        topicsList.add(subTopic);
      }
    });

    return currentTopic;
  };

  /**
   * Top level subscribe function digs through the topic tree and subscribes the
   * caller to any leaf nodes in the tree that they requested to subscribe to
   * @method subscribe
   * @param  {string} topics: string 'fruits.apple,fruits.banana'
   * @param  {Function} handler: SubscriberHandler Handles messages on publish
   * @param  {boolean} replayMessage A switch to turn on/off saved data replay
   * @return {Function} Reverses the effects of subscribe
   */
  subscribe = (
    topics: string,
    handler: SubscriberHandler,
    replayMessage: boolean = true
  ): ((clearTopic?: boolean) => void) => {
    this.logToRecents('subscriptions', topics, NUM_RECENT);

    // Make sure all topic strings are lowercased
    const topicsList: string[] = topics.toLowerCase().split(Constants.SIBLING_SEPARATOR);

    logger.log('New subscribe request for topics %o', topicsList);
    const unsubscribers: (() => void)[] = [];

    // Now we split the topic string into siblings, and then into nested topics
    // and we subscribe only to the leaf names provided
    topicsList.forEach(topic => {
      const currentTopic = this.findLeafTopic(topic);

      // We've found the last topic in the list, so subscribe and push it
      // onto the subscribers list
      unsubscribers.push(currentTopic.subscribe(handler, replayMessage));
    });

    // Returns a function that calls of the unsubscribe functions that we
    // got back from subscribing to various topics
    return (clearTopic: boolean = false) => {
      unsubscribers.forEach(us => {
        logger.log('Unsubscribing function');
        us();
      });
      if (clearTopic) {
        this.clearTopics(topics);
      }
    };
  };

  publish = <T>(topics: string, type?: string, data?: T, saveMessage?: boolean): void => {
    // Make sure all topic strings are lowercased
    const topicsList: string[] = topics.toLowerCase().split(Constants.SIBLING_SEPARATOR);

    logger.log(
      'New "%s" message published to topics %o with content %o',
      type,
      topicsList,
      data
    );

    // Keep track of the last NUM_RECENT broker calls
    this.logToRecents(
      'recentTopics',
      { type, topics, timestamp: Date.now() },
      NUM_RECENT
    );

    // Create the set of topics to publish to so we can add to it
    const topicsToPublishTo: Set<Topic> = new Set();
    topicsList.forEach(topic => {
      this.findLeafTopic(topic, topicsToPublishTo);
    });

    // Now publish to the set of topics we created
    topicsToPublishTo.forEach(topic =>
      topic.publish(topics, type || '', data, saveMessage)
    );
  };

  /**
   * Clear all stored messages in a topic
   */
  clearTopics = (topics: string): void => {
    const topicsList: string[] = topics.toLowerCase().split(Constants.SIBLING_SEPARATOR);

    logger.log('Clearing topics %o', topicsList);

    // Create the set of topics to publish to so we can add to it
    const topicsToClear: Set<Topic> = new Set();
    topicsList.forEach(topic => {
      this.findLeafTopic(topic, topicsToClear);
    });

    // Now clear messages in topic
    topicsToClear.forEach(topic => topic.clearTopics());
  };

  /**
   * Registers a special "handler" function to be called when the .call() method
   * is invoked. A `RegistrantHandler` MUST return a promise.
   * @param  {string} topics  A string of the form fruits.apples,fruits.bananas
   * @param  {Function} handler A handler function that returns a promise
   * @return {Function}   Unregisters all handlers that are registred.
   */
  register = (topics: string, handler: RegistrantHandler): (() => void) => {
    this.logToRecents('registrations', topics, NUM_RECENT);

    // Make sure all topic strings are lowercased
    const topicsList: string[] = topics.toLowerCase().split(Constants.SIBLING_SEPARATOR);

    logger.log('New register request for topics %o', topicsList);
    const unregisterCbs: (() => void)[] = [];

    // Process each sibling topic path and register against the leaf nodes
    topicsList.forEach(topic =>
      unregisterCbs.push(this.findLeafTopic(topic).register(handler))
    );

    return () => unregisterCbs.forEach(ur => ur());
  };

  /**
   * Calls the previously registered handler on a topic and returns a promise that
   * will be resolved once the handler has finished its work
   * @param  {string} topicPath A single, potentially nested, topicPath. No
   * siblings are allowed.
   * @param  {T} arg The data to pass to the previously registered handler.
   * @return {Promise<any>}   A promise that gets resolved when the registered
   * handler resolves
   */
  call = (topicPath: string, arg?: any): Promise<any> => {
    const normalizedTopicPath = topicPath.toLowerCase();
    if (normalizedTopicPath.includes(Constants.SIBLING_SEPARATOR)) {
      // topicPath should not include any siblings, throw an error
      throw new Error(
        `Only 1 leaf topic to call should be provided to the
      topicPath parameter. "topicPath" was: ${topicPath}`
      );
    }
    return this.findLeafTopic(normalizedTopicPath).call(arg);
  };
}

const Broker = new TheBroker();

/**
 * Expose logger function onto the window object to allow us to inspect
 * and/or make available for a part of the application
 *
 * This gets attached to the window in src/clients/main
 */
export const logRecentBroker = () => {
  recentLogger.log('Broker recent topics', Broker.recentTopics);
  recentLogger.log('Broker recent subscriptions', Broker.subscriptions);
  recentLogger.log('Broker recent registrations', Broker.registrations);
};

export default Broker;
